<script lang="ts">
  /**
   * Trang gallery ảnh render CÔNG KHAI cho khách (route /v/{token}/gallery).
   * Chỉ hiển thị ảnh render ĐÃ LƯU (endpoint công khai, không cần auth) — KHÔNG tải model 3D.
   * Tái dùng RenderGallery (chế độ standalone): lưới thumbnail → lightbox có ‹ › (desktop),
   * vuốt (mobile), phím ←/→, Esc.
   */
  import { onMount } from 'svelte';
  import type { RenderDto } from '@wood-viewer/shared';
  import { ApiError, listRenders } from './lib/api/client';
  import { t } from './i18n/index.svelte';
  import RenderGallery from './ui/RenderGallery.svelte';

  let { token, authKey = null }: { token: string; authKey?: string | null } = $props();

  // Trang gallery có vòng đời tải RIÊNG (App.svelte thoát sớm ở route gallery → không chạy classify).
  // Nên tự phân loại 403 private tại đây (xem WO-04a §3.3).
  type Status = 'loading' | 'ready' | 'error';
  let status = $state<Status>('loading');
  let errorKind = $state<'private' | 'network'>('network');
  let renders = $state<RenderDto[]>([]);

  async function load(): Promise<void> {
    status = 'loading';
    try {
      renders = await listRenders(token, authKey);
      status = 'ready';
    } catch (e) {
      // Model private + không key (403) → màn "riêng tư"; còn lại → lỗi mạng (có nút thử lại).
      errorKind = e instanceof ApiError && e.status === 403 && !authKey ? 'private' : 'network';
      status = 'error';
    }
  }
  onMount(load);
</script>

{#if status === 'ready'}
  <RenderGallery {renders} standalone />
{:else}
  <main class="state-shell">
    {#if status === 'loading'}
      <div class="loading">
        <div class="spinner" aria-hidden="true"></div>
        <p>{t('app.loading')}</p>
      </div>
    {:else}
      <div class="card">
        <h1>{t(`err.${errorKind}.title`)}</h1>
        <p>{t(`err.${errorKind}.body`)}</p>
        {#if errorKind === 'network'}
          <button class="retry" onclick={load}>{t('app.retry')}</button>
        {/if}
      </div>
    {/if}
  </main>
{/if}

<style>
  .state-shell {
    position: fixed;
    inset: 0;
    display: grid;
    place-items: center;
    background: linear-gradient(var(--color-canvas-top), var(--color-canvas-bottom));
    padding: var(--space-lg);
  }
  .card {
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-panel);
    box-shadow: var(--shadow-panel);
    padding: var(--space-xl);
    max-width: 380px;
    text-align: center;
  }
  h1 {
    margin: 0 0 var(--space-xs);
    font-size: var(--font-size-title);
    color: var(--color-navy);
  }
  p {
    margin: 0;
    color: var(--color-text-secondary);
    line-height: 1.5;
  }
  .retry {
    margin-top: var(--space-md);
    min-height: var(--size-touch-min);
    padding: 0 var(--space-lg);
    border: none;
    border-radius: var(--radius-control);
    background: var(--color-primary);
    color: #fff;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
  }
  .retry:hover {
    background: var(--color-primary-accent);
  }
  .loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-md);
    color: var(--color-text-secondary);
  }
  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--color-border);
    border-top-color: var(--color-primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
</style>
